import React from 'react';

interface AgreementDisplayProps {
  content: string;
  className?: string;
  selectable?: boolean;
}

function parseInlineFormatting(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <strong key={match.index} className="font-bold">
        {match[1]}
      </strong>
    );
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

export const AgreementDisplay: React.FC<AgreementDisplayProps> = ({ content, className = '', selectable = false }) => {
  const isDark = className.includes('text-gray-100');

  const renderFormattedContent = (text: string) => {
    const lines = text.split('\n');
    const elements: JSX.Element[] = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        elements.push(<div key={index} className="h-3" />);
        return;
      }

      if (line.includes('====')) {
        elements.push(
          <div key={index} className={`border-t-2 ${isDark ? 'border-gray-600' : 'border-gray-300'} my-6`} />
        );
        return;
      }

      if (line.includes('----')) {
        elements.push(
          <div key={index} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} my-4`} />
        );
        return;
      }

      const mdH1 = trimmedLine.match(/^#\s+(.+)/);
      if (mdH1) {
        elements.push(
          <h2 key={index} className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mt-8 mb-4 tracking-tight`}>
            {parseInlineFormatting(mdH1[1])}
          </h2>
        );
        return;
      }

      const mdH2 = trimmedLine.match(/^##\s+(.+)/);
      if (mdH2) {
        elements.push(
          <h3 key={index} className={`text-lg font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'} mt-7 mb-3 tracking-tight`}>
            {parseInlineFormatting(mdH2[1])}
          </h3>
        );
        return;
      }

      const mdH3 = trimmedLine.match(/^###\s+(.+)/);
      if (mdH3) {
        elements.push(
          <h4 key={index} className={`text-base font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'} mt-6 mb-2`}>
            {parseInlineFormatting(mdH3[1])}
          </h4>
        );
        return;
      }

      if (/^[A-Z][A-Z\s']{3,}$/.test(trimmedLine) && trimmedLine.length < 80) {
        elements.push(
          <h2 key={index} className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} mt-8 mb-4 uppercase tracking-wide text-center`}>
            {trimmedLine}
          </h2>
        );
        return;
      }

      if (trimmedLine.match(/^(TABLE OF CONTENTS|DOCUMENT CONTROL|VERSION|CONTENTS)/i)) {
        elements.push(
          <h2 key={index} className={`text-lg font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'} mt-6 mb-3 uppercase tracking-wide`}>
            {trimmedLine}
          </h2>
        );
        return;
      }

      if (trimmedLine.match(/^(SCHEDULE|APPENDIX|RECITALS|SIGNATURES?|EXECUTION)/i)) {
        elements.push(
          <h2 key={index} className={`text-lg font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'} mt-8 mb-4 uppercase tracking-wide border-b ${isDark ? 'border-gray-600' : 'border-gray-300'} pb-2`}>
            {trimmedLine}
          </h2>
        );
        return;
      }

      if (trimmedLine.match(/^\d+\.\s+[A-Z]/) && trimmedLine.length < 120) {
        elements.push(
          <h3 key={index} className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'} mt-6 mb-3 uppercase tracking-wide`}>
            {parseInlineFormatting(trimmedLine)}
          </h3>
        );
        return;
      }

      if (trimmedLine.match(/^\d+\.\d+/)) {
        const depthMatch = trimmedLine.match(/^(\d+(?:\.\d+)*)/);
        const depth = depthMatch ? depthMatch[1].split('.').length - 1 : 0;
        const marginLeft = Math.min(depth, 3) * 20;

        elements.push(
          <div key={index} className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'} mt-3 mb-2 text-sm leading-relaxed`} style={{ marginLeft: `${marginLeft}px` }}>
            {parseInlineFormatting(trimmedLine)}
          </div>
        );
        return;
      }

      if (trimmedLine.match(/^\([a-z]\)|^\([ivx]+\)|^[a-z]\)/)) {
        elements.push(
          <div key={index} className={`${isDark ? 'text-gray-300' : 'text-gray-700'} ml-10 my-1 text-sm leading-relaxed`}>
            {parseInlineFormatting(trimmedLine)}
          </div>
        );
        return;
      }

      if (trimmedLine.match(/^[•\-\*]\s/)) {
        elements.push(
          <div key={index} className={`${isDark ? 'text-gray-300' : 'text-gray-700'} ml-6 my-1.5 flex text-sm leading-relaxed`}>
            <span className="mr-2 text-gray-400">&#8226;</span>
            <span>{parseInlineFormatting(trimmedLine.replace(/^[•\-\*]\s/, ''))}</span>
          </div>
        );
        return;
      }

      if (trimmedLine.match(/^(Signed|Date|Witness|Name|Address|Print Name):/i)) {
        elements.push(
          <div key={index} className={`${isDark ? 'text-gray-100' : 'text-gray-900'} mt-4 mb-2 font-semibold text-sm`}>
            {parseInlineFormatting(trimmedLine)}
          </div>
        );
        return;
      }

      if (trimmedLine.match(/^_{3,}|^\.{3,}/)) {
        elements.push(
          <div key={index} className={`${isDark ? 'text-gray-500' : 'text-gray-400'} my-2 text-sm`}>
            {trimmedLine}
          </div>
        );
        return;
      }

      const sectionHeading = trimmedLine.match(/^(Section|Part|Chapter|Article|Clause)\s+\d+[:\s\-.]?\s*(.*)/i);
      if (sectionHeading && trimmedLine.length < 100) {
        elements.push(
          <h3 key={index} className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'} mt-6 mb-3`}>
            {parseInlineFormatting(trimmedLine)}
          </h3>
        );
        return;
      }

      if (
        trimmedLine.length > 3 &&
        trimmedLine.length < 80 &&
        !trimmedLine.match(/[.!?,;]$/) &&
        lines[index + 1]?.trim() === '' &&
        (index === 0 || lines[index - 1]?.trim() === '')
      ) {
        const words = trimmedLine.split(/\s+/);
        const capitalizedCount = words.filter(w => w[0] && w[0] === w[0].toUpperCase() && w[0] !== w[0].toLowerCase()).length;
        if (words.length <= 8 && capitalizedCount >= words.length * 0.6) {
          elements.push(
            <h3 key={index} className={`text-base font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'} mt-6 mb-3`}>
              {parseInlineFormatting(trimmedLine)}
            </h3>
          );
          return;
        }
      }

      const indentSpaces = line.length - line.trimStart().length;
      const indentLevel = Math.floor(indentSpaces / 4);
      const marginLeft = Math.min(indentLevel, 4) * 16;

      elements.push(
        <p key={index} className={`${isDark ? 'text-gray-300' : 'text-gray-700'} leading-relaxed my-1.5 text-sm`} style={{ marginLeft: `${marginLeft}px` }}>
          {parseInlineFormatting(trimmedLine)}
        </p>
      );
    });

    return elements;
  };

  const selectStyles = selectable
    ? {}
    : { userSelect: 'none' as const, WebkitUserSelect: 'none' as const, MozUserSelect: 'none' as const, msUserSelect: 'none' as const };

  return (
    <div className={`font-serif ${selectable ? '' : 'select-none'} ${className}`} style={selectStyles}>
      {renderFormattedContent(content)}
    </div>
  );
};
